// Resumen en texto de la sección Indicadores.
//
// Convierte los agregados del endpoint /summary en 2-4 frases. Es una función
// pura (payload + t → string[]) para poder razonarla y testearla sin montar la
// vista.
//
// Criterio de honestidad: las medianas se nombran como medianas ("la mitad
// de…", "habitualmente"), NO como promedios. Son cosas distintas y el usuario
// toma decisiones operativas con esto. Una frase que no tiene dato detrás
// simplemente no se emite.

import { formatCount, formatHours, formatLocalTime, formatPct, minutesToHHmm, resolveOperatorName } from "./format";

const MAX_SENTENCES = 4;

/**
 * @param {object|null} summary payload de /commerce-indicators/summary
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]} entre 0 y 4 frases listas para renderizar
 */
export function buildSummarySentences(summary, t) {
  if (!summary) return [];

  const sentences = [];
  const cutoffHour = summary?.thresholds?.deliveryCutoffHour;
  const cutoffLabel = minutesToHHmm(typeof cutoffHour === "number" ? cutoffHour * 60 : null);

  // 1. ¿A qué hora retira mi operador?
  const collection = summary.collection || {};
  if (collection.medianLocalTime && collection.count > 0) {
    sentences.push(
      t("La mitad de tus {{n}} retiros ocurrió antes de las {{hora}}.", {
        n: formatCount(collection.count),
        hora: formatLocalTime(collection.medianLocalTime),
      })
    );
  }

  // 2. ¿A qué hora se entregan mis pedidos?
  const delivery = summary.delivery || {};
  if (delivery.medianLocalTime && delivery.count > 0) {
    sentences.push(
      delivery.p90LocalTime
        ? t("Tus pedidos se entregan habitualmente a las {{hora}} y 9 de cada 10 ya están entregados antes de las {{p90}}.", {
            hora: formatLocalTime(delivery.medianLocalTime),
            p90: formatLocalTime(delivery.p90LocalTime),
          })
        : t("Tus pedidos se entregan habitualmente a las {{hora}}.", {
            hora: formatLocalTime(delivery.medianLocalTime),
          })
    );
  }

  // 3. Entregas después del corte (o su ausencia, que también es información).
  const afterCutoff = delivery.afterCutoff || {};
  if (afterCutoff.pct !== null && afterCutoff.pct !== undefined && delivery.count > 0) {
    sentences.push(
      afterCutoff.count > 0
        ? t("El {{pct}} de tus entregas ocurre después de las {{corte}} ({{n}} de {{total}}).", {
            pct: formatPct(afterCutoff.pct),
            corte: cutoffLabel,
            n: formatCount(afterCutoff.count),
            total: formatCount(delivery.count),
          })
        : t("Ninguna entrega ocurrió después de las {{corte}} en este período.", { corte: cutoffLabel })
    );
  }

  // 4. On-time — si no hay, cae al lead time, que responde la misma inquietud
  //    ("cuánto tarda mi pedido") con otro ángulo.
  const onTime = summary.onTime || {};
  const leadTime = summary.leadTime || {};
  if (onTime.sameDayBeforeCutoffPct !== null && onTime.sameDayBeforeCutoffPct !== undefined) {
    sentences.push(
      t("El {{pct}} de los pedidos se entrega el mismo día antes de las {{corte}} ({{matched}} de {{eligible}} evaluados).", {
        pct: formatPct(onTime.sameDayBeforeCutoffPct),
        corte: cutoffLabel,
        matched: formatCount(onTime.matched),
        eligible: formatCount(onTime.eligible),
      })
    );
  } else if (leadTime.medianHours !== null && leadTime.medianHours !== undefined) {
    sentences.push(
      t("Desde que se crea el pedido hasta que se entrega pasan habitualmente {{horas}}.", {
        horas: formatHours(leadTime.medianHours),
      })
    );
  }

  // 5. Operador con más entregas fuera de corte — solo tiene sentido si hay
  //    más de uno con quien compararlo.
  const byOperator = Array.isArray(summary.byOperator) ? summary.byOperator : [];
  if (byOperator.length > 1) {
    const worst = byOperator
      .filter((op) => typeof op?.afterCutoffPct === "number" && op.afterCutoffPct > 0)
      .sort((a, b) => b.afterCutoffPct - a.afterCutoffPct)[0];
    if (worst) {
      sentences.push(
        t("{{operador}} es quien más entrega después de las {{corte}}: {{pct}} de sus {{n}} entregas.", {
          operador: resolveOperatorName(worst, t),
          corte: cutoffLabel,
          pct: formatPct(worst.afterCutoffPct),
          n: formatCount(worst.deliveries),
        })
      );
    }
  }

  return sentences.slice(0, MAX_SENTENCES);
}
